import { ChatType } from '@/data/models/domain/chats/types'
import { isUnicode } from '@/utils/string/isUnicode'
import { getUnicodeChars } from '@/utils/string/getUnicodeChars'

const maxParts = 6
const GSMSymbolsRegExp = /[{}\\[\]|€~^]/g
const unicodeCharsOnePart = 67
const unicodeRestCharsOnePart = 3
const notUnicodeCharsOnePart = 153
const notUnicodeRestCharsOnePart = 7

const charsLimitExternalMessenger = 4096
const charsLimitFacebookMessenger = 2000
const charsLimitInstagram = 1000

export type SmsInfo = {
  isUnicodeText: boolean
  overflow: boolean
  chars: number
  maxChars: number
  parts: number
  maxParts: number
  unicodeChars: string[]
}

const calcSmsInfoParts = (chars: number, charsInOnePart: number, restCharsInOnePart: number): number => {
  if (chars === 0) return 0
  if (chars > charsInOnePart + restCharsInOnePart) return Math.ceil(chars / charsInOnePart)
  return 1
}

export const getSmsChars = (msg: string, unicodeText = false) =>
  unicodeText ? msg.length : msg.length + (msg.match(GSMSymbolsRegExp) || []).length

export const getSmsInfo = (msg: string, chatType?: ChatType): SmsInfo => {
  const isUnicodeText = isUnicode(msg)
  let charsInOnePart = 0
  let unicodeChars: string[] = []
  let maxChars = 0
  if (chatType === ChatType.Instagram) {
    charsInOnePart = charsLimitInstagram
    maxChars = charsInOnePart
  } else if (chatType === ChatType.Facebook) {
    charsInOnePart = charsLimitFacebookMessenger
    maxChars = charsInOnePart
  } else if (isExternalMessenger(chatType)) {
    charsInOnePart = charsLimitExternalMessenger
    maxChars = charsInOnePart
  } else {
    charsInOnePart = isUnicodeText ? unicodeCharsOnePart : notUnicodeCharsOnePart
    maxChars = maxParts * charsInOnePart
  }
  const restCharsInOnePart = isUnicodeText ? unicodeRestCharsOnePart : notUnicodeRestCharsOnePart
  const chars = getSmsChars(msg, isUnicodeText)
  const parts = calcSmsInfoParts(chars, charsInOnePart, restCharsInOnePart)
  const overflow = chars > maxChars
  if (isUnicodeText) {
    unicodeChars = getUnicodeChars(msg)
  }

  return {
    isUnicodeText,
    overflow,
    chars,
    maxChars,
    parts,
    maxParts,
    unicodeChars,
  }
}

const externalMessengersArray = [ChatType.WhatsAppBusiness, ChatType.Facebook, ChatType.Instagram] as const

export const externalMessengers = new Set<ChatType | undefined>(externalMessengersArray)
export const isExternalMessenger = (chatType?: ChatType): chatType is (typeof externalMessengersArray)[number] => {
  return externalMessengers.has(chatType)
}
